import { create } from 'zustand'

import type { CrowdActionEvent, CrowdAvatar } from './types'

const SEEN_IDS_MAX = 200
const seenActionIds = new Set<string>()

function rememberActionId(id: string): boolean {
  if (seenActionIds.has(id)) return false
  seenActionIds.add(id)
  if (seenActionIds.size > SEEN_IDS_MAX) {
    const first = seenActionIds.values().next().value
    if (first !== undefined) seenActionIds.delete(first)
  }
  return true
}

interface CrowdAvatarsState {
  avatars: CrowdAvatar[]
  actionQueue: CrowdActionEvent[]
  /** Streamer overlay setting — mute crowd SFX when false. */
  sfxEnabled: boolean
  setAvatars: (avatars: CrowdAvatar[]) => void
  upsertAvatar: (avatar: CrowdAvatar) => void
  setSfxEnabled: (enabled: boolean) => void
  enqueueAction: (action: CrowdActionEvent) => void
  shiftAction: () => CrowdActionEvent | null
}

export const useCrowdAvatarsStore = create<CrowdAvatarsState>((set, get) => ({
  avatars: [],
  actionQueue: [],
  sfxEnabled: true,

  setAvatars: (avatars) => set({ avatars }),

  upsertAvatar: (avatar) =>
    set((s) => {
      const idx = s.avatars.findIndex((a) => a.id === avatar.id)
      if (idx < 0) return { avatars: [...s.avatars, avatar] }
      const next = s.avatars.slice()
      next[idx] = avatar
      return { avatars: next }
    }),

  setSfxEnabled: (sfxEnabled) => set({ sfxEnabled }),

  enqueueAction: (action) =>
    set((s) => {
      if (!rememberActionId(action.id)) return s
      if (s.actionQueue.some((a) => a.id === action.id)) return s
      return { actionQueue: [...s.actionQueue, action] }
    }),

  shiftAction: () => {
    const [first, ...rest] = get().actionQueue
    set({ actionQueue: rest })
    return first ?? null
  },
}))
